import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Megaphone, Plus, Trash2, Image, X, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BachecaPage = () => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imagePath, setImagePath] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAnnouncements();
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/check`, { withCredentials: true });
      setIsAdmin(res.data.is_admin);
    } catch { setIsAdmin(false); }
  };

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/announcements`, { withCredentials: true });
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Seleziona un'immagine valida");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Immagine troppo grande. Max 10MB');
      return;
    }

    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/api/announcements/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImagePath(res.data.image_path);
      toast.success('Immagine caricata');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error("Errore nel caricamento dell'immagine");
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setImagePath(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Inserisci un titolo'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/announcements`, {
        title: title.trim(),
        content: content.trim(),
        image_path: imagePath
      }, { withCredentials: true });

      toast.success('Annuncio pubblicato!');
      setTitle('');
      setContent('');
      setImagePath(null);
      setImagePreview(null);
      setShowForm(false);
      fetchAnnouncements();
    } catch (error) {
      console.error('Error creating announcement:', error);
      toast.error(error.response?.data?.detail || "Errore nella pubblicazione");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (announcementId) => {
    try {
      await axios.delete(`${API_URL}/api/announcements/${announcementId}`, { withCredentials: true });
      toast.success('Annuncio eliminato');
      setAnnouncements(prev => prev.filter(a => a.announcement_id !== announcementId));
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error("Errore nell'eliminazione");
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMMM yyyy 'alle' HH:mm", { locale: it });
    } catch { return dateStr; }
  };

  const getImageUrl = (path) => `${API_URL}/api/files/${path}`;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto animate-fadeIn" data-testid="bacheca-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center">
            <Megaphone className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="font-outfit text-2xl font-semibold text-slate-900">
              Bacheca del Circolo
            </h1>
            <p className="text-sm text-slate-500">Annunci e comunicazioni</p>
          </div>
        </div>

        {isAdmin && (
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="new-announcement-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-full font-medium hover:bg-amber-600 transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? 'Chiudi' : 'Nuovo Annuncio'}
          </button>
        )}
      </div>

      {/* Create Form (Admin only) */}
      {isAdmin && showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 border border-amber-200 mb-8 space-y-4" data-testid="announcement-form">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Titolo *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="announcement-title-input"
              placeholder="Es: Torneo di primavera"
              className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-all outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Descrizione</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              data-testid="announcement-content-input"
              placeholder="Scrivi il tuo annuncio..."
              rows={4}
              className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-all outline-none resize-none"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Immagine (opzionale)</label>
            {imagePreview ? (
              <div className="relative rounded-2xl overflow-hidden border border-slate-200">
                <img src={imagePreview} alt="Anteprima" className="w-full max-h-64 object-cover" />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-3 right-3 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                >
                  <X className="w-4 h-4" />
                </button>
                {uploadingImage && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="announcement-image-btn"
                className="w-full py-8 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 hover:border-amber-300 hover:text-amber-500 transition-colors flex flex-col items-center gap-2"
              >
                <Image className="w-8 h-8" />
                <span className="text-sm">Clicca per aggiungere un'immagine</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || uploadingImage}
            data-testid="publish-announcement-btn"
            className="w-full py-3.5 bg-amber-500 text-white rounded-full font-medium hover:bg-amber-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Megaphone className="w-5 h-5" />
                Pubblica Annuncio
              </>
            )}
          </button>
        </form>
      )}

      {/* Announcements List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 border-3 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-slate-100">
          <Megaphone className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">Nessun annuncio</p>
          <p className="text-slate-400 text-sm mt-1">
            {isAdmin ? 'Pubblica il primo annuncio del circolo!' : 'Il maestro non ha ancora pubblicato annunci'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {announcements.map((ann) => (
            <article
              key={ann.announcement_id}
              data-testid={`announcement-${ann.announcement_id}`}
              className="bg-white rounded-3xl border border-slate-100 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Announcement Image */}
              {ann.image_path && (
                <div className="w-full max-h-80 overflow-hidden">
                  <img
                    src={getImageUrl(ann.image_path)}
                    alt={ann.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}

              <div className="p-6">
                {/* Header with title and delete */}
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">
                    {ann.title}
                  </h2>
                  {isAdmin && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`delete-announcement-${ann.announcement_id}`}
                          className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminare questo annuncio?</AlertDialogTitle>
                          <AlertDialogDescription>
                            L'annuncio "{ann.title}" verrà eliminato definitivamente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(ann.announcement_id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Elimina
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>

                {/* Content */}
                {ann.content && (
                  <p className="text-slate-600 whitespace-pre-wrap leading-relaxed mb-4">
                    {ann.content}
                  </p>
                )}

                {/* Footer */}
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{formatDate(ann.created_at)}</span>
                  <span className="mx-1">·</span>
                  <span>{ann.author_name}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default BachecaPage;
